import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Truck, Plus, Loader2, X, Mail, Phone } from "lucide-react";
import { api, formatApiError } from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import { ModuleGate } from "@/components/PlanNotIncluded";

type Vendor = {
    id: string; name: string; contact_name: string | null; phone: string | null;
    email: string | null; tax_id: string | null; notes: string | null; is_active: boolean;
};

export default function Vendors() {
    const { t } = useTranslation();
    const { hasPerm } = useAuth();
    const canCreate = hasPerm("procurement.create");
    const [rows, setRows] = useState<Vendor[]>([]);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState<string | null>(null);
    const [creating, setCreating] = useState(false);
    const [form, setForm] = useState({ name: "", contact_name: "", phone: "", email: "", tax_id: "", notes: "" });
    const [saving, setSaving] = useState(false);

    const load = async () => {
        setLoading(true); setErr(null);
        try {
            const r = await api.get("/procurement/vendors");
            setRows(r.data.items || []);
        } catch (e: any) { setErr(formatApiError(e)); } finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const submit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!form.name.trim()) return;
        setSaving(true); setErr(null);
        try {
            await api.post("/procurement/vendors", {
                name: form.name.trim(),
                contact_name: form.contact_name.trim() || null,
                phone: form.phone.trim() || null,
                email: form.email.trim() || null,
                tax_id: form.tax_id.trim() || null,
                notes: form.notes.trim() || null,
            });
            setCreating(false);
            setForm({ name: "", contact_name: "", phone: "", email: "", tax_id: "", notes: "" });
            await load();
        } catch (e: any) { setErr(formatApiError(e)); } finally { setSaving(false); }
    };

    return (
        <ModuleGate module="procurement">
            <div className="space-y-4" data-testid="proc-vendors-page">
                <header className="flex items-center gap-3 flex-wrap">
                    <Truck size={22} style={{ color: "var(--primary)" }} />
                    <h1 className="text-xl font-semibold flex-1" style={{ color: "var(--text)" }}>{t("procurement.vendors.title")}</h1>
                    {canCreate && (
                        <button data-testid="proc-vendor-new-btn" onClick={() => setCreating(true)}
                            className="inline-flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium"
                            style={{ background: "var(--primary)", color: "var(--primary-fg)", minHeight: 40 }}>
                            <Plus size={16} /> {t("procurement.vendor.new")}
                        </button>
                    )}
                </header>
                {err && <div data-testid="proc-vendor-err" className="rounded-md p-3 text-sm" style={{ background: "var(--error-bg)", color: "var(--error-fg)" }}>{err}</div>}
                {loading && <div className="flex justify-center p-6"><Loader2 className="animate-spin" size={22} /></div>}
                {!loading && !rows.length && <div className="surface-card p-6 rounded-xl text-sm" style={{ color: "var(--text-muted)", background: "var(--card)", border: "1px solid var(--border)" }} data-testid="proc-vendors-empty">{t("procurement.vendors.empty")}</div>}
                {!!rows.length && (
                    <div className="overflow-x-auto rounded-xl border" style={{ borderColor: "var(--border)", background: "var(--card)" }}>
                        <table className="w-full text-sm">
                            <thead>
                                <tr style={{ color: "var(--text-muted)", borderBottom: "1px solid var(--border)" }}>
                                    <th className="text-left px-3 py-2">{t("procurement.vendor.name")}</th>
                                    <th className="text-left px-3 py-2">{t("procurement.vendor.contact")}</th>
                                    <th className="text-left px-3 py-2">{t("procurement.vendor.phone")}</th>
                                    <th className="text-left px-3 py-2">{t("procurement.vendor.email")}</th>
                                    <th className="text-left px-3 py-2">{t("procurement.vendor.tax_id")}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((v) => (
                                    <tr key={v.id} className="border-t" style={{ borderColor: "var(--border)" }} data-testid={`proc-vendor-row-${v.id}`}>
                                        <td className="px-3 py-2" style={{ color: "var(--text)" }}>{v.name}</td>
                                        <td className="px-3 py-2" style={{ color: "var(--text-muted)" }}>{v.contact_name || "—"}</td>
                                        <td className="px-3 py-2" style={{ color: "var(--text-muted)" }}>{v.phone ? <span className="inline-flex items-center gap-1"><Phone size={12} />{v.phone}</span> : "—"}</td>
                                        <td className="px-3 py-2" style={{ color: "var(--text-muted)" }}>{v.email ? <span className="inline-flex items-center gap-1"><Mail size={12} />{v.email}</span> : "—"}</td>
                                        <td className="px-3 py-2" style={{ color: "var(--text-muted)" }}>{v.tax_id || "—"}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {creating && (
                    <div className="fixed inset-0 z-40 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.55)" }} onClick={() => setCreating(false)}>
                        <div className="w-full max-w-lg rounded-xl border p-5" style={{ background: "var(--card)", borderColor: "var(--border)" }} onClick={(e) => e.stopPropagation()} data-testid="proc-vendor-modal">
                            <div className="flex items-center gap-2 mb-4">
                                <h2 className="text-lg font-semibold flex-1" style={{ color: "var(--text)" }}>{t("procurement.vendor.new")}</h2>
                                <button aria-label="close" onClick={() => setCreating(false)} data-testid="proc-vendor-modal-close"><X size={18} style={{ color: "var(--text-muted)" }} /></button>
                            </div>
                            <form onSubmit={submit} className="space-y-3">
                                <label className="block text-xs" style={{ color: "var(--text-muted)" }}>
                                    <span>{t("procurement.vendor.name")} *</span>
                                    <input required data-testid="proc-vendor-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                                        className="mt-1 w-full rounded-md px-3 py-2 text-sm" style={{ background: "var(--input)", color: "var(--text)", border: "1px solid var(--border)", minHeight: 40 }} />
                                </label>
                                <div className="grid grid-cols-2 gap-3">
                                    <label className="block text-xs" style={{ color: "var(--text-muted)" }}>
                                        <span>{t("procurement.vendor.contact")}</span>
                                        <input data-testid="proc-vendor-contact" value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })}
                                            className="mt-1 w-full rounded-md px-3 py-2 text-sm" style={{ background: "var(--input)", color: "var(--text)", border: "1px solid var(--border)", minHeight: 40 }} />
                                    </label>
                                    <label className="block text-xs" style={{ color: "var(--text-muted)" }}>
                                        <span>{t("procurement.vendor.phone")}</span>
                                        <input data-testid="proc-vendor-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                                            className="mt-1 w-full rounded-md px-3 py-2 text-sm" style={{ background: "var(--input)", color: "var(--text)", border: "1px solid var(--border)", minHeight: 40 }} />
                                    </label>
                                    <label className="block text-xs" style={{ color: "var(--text-muted)" }}>
                                        <span>{t("procurement.vendor.email")}</span>
                                        <input type="email" data-testid="proc-vendor-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                                            className="mt-1 w-full rounded-md px-3 py-2 text-sm" style={{ background: "var(--input)", color: "var(--text)", border: "1px solid var(--border)", minHeight: 40 }} />
                                    </label>
                                    <label className="block text-xs" style={{ color: "var(--text-muted)" }}>
                                        <span>{t("procurement.vendor.tax_id")}</span>
                                        <input data-testid="proc-vendor-taxid" value={form.tax_id} onChange={(e) => setForm({ ...form, tax_id: e.target.value })}
                                            className="mt-1 w-full rounded-md px-3 py-2 text-sm" style={{ background: "var(--input)", color: "var(--text)", border: "1px solid var(--border)", minHeight: 40 }} />
                                    </label>
                                </div>
                                <label className="block text-xs" style={{ color: "var(--text-muted)" }}>
                                    <span>{t("common.notes")}</span>
                                    <textarea data-testid="proc-vendor-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2}
                                        className="mt-1 w-full rounded-md px-3 py-2 text-sm" style={{ background: "var(--input)", color: "var(--text)", border: "1px solid var(--border)" }} />
                                </label>
                                <div className="flex justify-end gap-2 pt-2">
                                    <button type="button" onClick={() => setCreating(false)} data-testid="proc-vendor-cancel" className="rounded-md px-3 py-2 text-sm" style={{ background: "var(--input)", color: "var(--text-muted)", minHeight: 40 }}>{t("common.cancel")}</button>
                                    <button type="submit" disabled={saving} data-testid="proc-vendor-save" className="rounded-md px-3 py-2 text-sm font-medium" style={{ background: "var(--primary)", color: "var(--primary-fg)", minHeight: 40 }}>
                                        {saving ? <Loader2 className="animate-spin" size={14} /> : t("common.save")}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}
            </div>
        </ModuleGate>
    );
}
